import { db, getMeta, setMeta } from './db'
import type { Outbox } from './types'

/**
 * The sync engine.
 *
 * The Cloudflare Access gotcha this is built around: when an Access session
 * expires, Access answers a fetch with an HTML login page and a redirect, not a
 * clean error. Sync code expecting JSON parses garbage, marks the batch failed,
 * and can silently drop queued expenses — on a phone, in the desert.
 *
 * Three guards, together:
 *   1. X-Requested-With: XMLHttpRequest, so Access answers 401 instead of HTML.
 *   2. A content-type check before parsing: not JSON means not our API.
 *   3. 401 pauses the drain and keeps the outbox intact. It is never a failure.
 */

const CURSOR = 'syncCursor'

export type SyncState =
  | { status: 'idle'; pending: number }
  | { status: 'syncing'; pending: number }
  | { status: 'offline'; pending: number }
  | { status: 'reauth'; pending: number }
  | { status: 'error'; pending: number; message: string }

type Listener = (s: SyncState) => void
const listeners = new Set<Listener>()
let state: SyncState = { status: 'idle', pending: 0 }

export function subscribe(fn: Listener): () => void {
  listeners.add(fn)
  fn(state)
  return () => listeners.delete(fn)
}

async function emit(next: { status: SyncState['status']; message?: string; pending?: number }) {
  const pending = next.pending ?? (await db.outbox.count())
  state = { ...next, pending } as SyncState
  for (const fn of listeners) fn(state)
}

class ReauthNeeded extends Error {}

async function apiFetch(path: string, init: RequestInit = {}): Promise<unknown> {
  const res = await fetch(path, {
    ...init,
    headers: {
      // Guard 1. Without this, an expired Access session returns HTML.
      'X-Requested-With': 'XMLHttpRequest',
      'content-type': 'application/json',
      ...(init.headers ?? {}),
    },
  })

  // Guard 3. Not a failed write — the session simply needs renewing.
  if (res.status === 401 || res.status === 403) throw new ReauthNeeded()

  // Guard 2. A redirect to a login page, or the SPA fallback, is not our API.
  const type = res.headers.get('content-type') ?? ''
  if (!type.includes('application/json')) {
    throw new ReauthNeeded()
  }

  if (!res.ok) throw new Error(`${path} returned ${res.status}`)
  return res.json()
}

/**
 * Queue a write — and apply it locally first.
 *
 * Every screen reads from IndexedDB, so the local write is what puts the row on
 * screen before the request leaves the phone. Queueing without applying was a
 * real bug: offline edits vanished until a pull brought them back from the
 * server, which offline is never.
 */
export async function enqueue(m: Omit<Outbox, 'opId' | 'queuedAt' | 'attempts'>): Promise<void> {
  const now = Date.now()
  const id = (m.row as { id?: string }).id
  const store = (db as unknown as Record<string, {
    get(k: string): Promise<Record<string, unknown> | undefined>
    put(v: unknown): Promise<unknown>
  }>)[m.table]

  if (store && id) {
    const existing = await store.get(id)
    await store.put(
      m.op === 'delete'
        ? { ...(existing ?? m.row), deleted_at: now, updated_at: now }
        : { ...(existing ?? {}), ...m.row, deleted_at: null, updated_at: now },
    )

    // Shares are replaced wholesale with the expense, so a split edit cannot
    // leave a stale share row summing to the wrong total.
    if (m.table === 'expenses' && m.shares) {
      await db.expense_shares.where('expense_id').equals(id).delete()
      await db.expense_shares.bulkPut(
        m.shares.map((s) => ({
          expense_id: id, user_id: s.user_id, share_minor: s.share_minor, updated_at: now,
        })),
      )
    }
  }

  await db.outbox.add({ ...m, opId: crypto.randomUUID(), queuedAt: now, attempts: 0 })
  void drain()
}

let draining = false

export async function drain(): Promise<void> {
  if (draining) return
  if (!navigator.onLine) return emit({ status: 'offline' })

  draining = true
  try {
    const batch = await db.outbox.orderBy('queuedAt').limit(200).toArray()
    if (batch.length) {
      await emit({ status: 'syncing' })
      const result = (await apiFetch('/api/sync', {
        method: 'POST',
        body: JSON.stringify({
          mutations: batch.map(({ opId, table, op, row, shares }) => ({ opId, table, op, row, shares })),
        }),
      })) as { applied: string[]; rejected: Array<{ opId: string; reason: string }> }

      // Only remove what the server acknowledged. Nothing is dropped locally
      // until it is durably stored on the other side.
      if (result.applied?.length) await db.outbox.bulkDelete(result.applied)

      // A malformed mutation is dropped rather than left to wedge the queue,
      // but it is loud about it.
      for (const r of result.rejected ?? []) {
        console.error('Sync rejected a mutation', r)
        await db.outbox.delete(r.opId)
      }
    }
    await pullChanges()
    await emit({ status: 'idle' })
  } catch (cause) {
    if (cause instanceof ReauthNeeded) {
      await emit({ status: 'reauth' })
    } else if (!navigator.onLine) {
      await emit({ status: 'offline' })
    } else {
      await emit({ status: 'error', message: (cause as Error).message })
    }
  } finally {
    draining = false
  }
}

export async function pullChanges(): Promise<void> {
  const since = await getMeta<number>(CURSOR, 0)
  const data = (await apiFetch(`/api/sync?since=${since}`)) as {
    now: number
    tables: Record<string, Array<Record<string, unknown>>>
  }

  // Never let a pull clobber a row that still has an unsynced local edit.
  const queued = await db.outbox.toArray()
  const locked = new Set(
    queued.map((m) => `${m.table}:${(m.row as { id?: string }).id ?? ''}`),
  )

  for (const [table, rows] of Object.entries(data.tables)) {
    if (!rows.length) continue
    const store = (db as unknown as Record<string, { bulkPut(r: unknown[]): Promise<unknown> }>)[table]
    if (!store) continue
    const safe = rows.filter((r) => !locked.has(`${table}:${String(r.id ?? '')}`))
    if (safe.length) await store.bulkPut(safe)
  }

  await setMeta(CURSOR, data.now)
}

/** Start the background loop. Foreground only — no point burning battery in the background. */
export function startSync(): () => void {
  const kick = () => void drain()

  window.addEventListener('online', kick)
  window.addEventListener('offline', () => void emit({ status: 'offline' }))
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') kick()
  })

  const timer = window.setInterval(() => {
    if (document.visibilityState === 'visible') kick()
  }, 60_000)

  kick()

  return () => {
    window.removeEventListener('online', kick)
    window.clearInterval(timer)
  }
}
